import React, { createContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { DashInviteRoleEnum } from "@/__generated__/types";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui";
import { PlusCircle, UserSearch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRoute } from "@/routes/getRoute";
import { EmptyState } from "@/components/DataTable/States/Empty";
import { i18n } from "@/i18n";
import { ROLE_MAP, useInviteTableConfig } from "./hooks/useInviteTableConfig";
import { useInvites } from "@/hooks/useInvites";
import { SeatAlert } from "./Components/SeatAlert";
import { CURRENT_USER_PERMISSIONS } from "@/api/queries/currentUserPermissions";
import { useQuery } from "@apollo/client";
import { PermissionAlert } from "./Components/PermissionAlert";
import { useOrgDetails } from "@/hooks/useOrgDetails";

export const InviteTableContext = createContext<{
  refetch?: () => void;
}>({});

const DEFAULT_ROWS = 5;

export const InviteTable = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "teamManagement.table",
  });
  const { org, loading: orgLoading } = useOrgDetails();
  const [pageIndex, setPageIndex] = useState(0);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [filterRoles, setFilterRoles] = useState<DashInviteRoleEnum[]>([]);
  const { columns, ordering } = useInviteTableConfig();
  const navigate = useNavigate();
  const getRoute = useRoute();

  const handleFilterChange = (filterId: string, values: string[]): void => {
    setPageIndex(0);
    setAfter(null);
    setBefore(null);
    setFilterRoles(values.map((value) => value as DashInviteRoleEnum));
  };

  const hasSeatsAvailable = Boolean(
    org?.seatsAvailable && org.seatsAvailable > 0,
  );

  const { data, loading, refetch } = useInvites({
    orgId: org?.id,
    roles: filterRoles,
    first: after || (!after && !before) ? DEFAULT_ROWS : undefined,
    last: before ? DEFAULT_ROWS : undefined,
    after,
    before,
    ordering: ordering,
  });

  const { data: permissionData } = useQuery(CURRENT_USER_PERMISSIONS, {
    variables: { organizationId: org?.id ?? "" },
    skip: !org?.id,
  });

  const { me } = permissionData ?? {};

  const {
    invites,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    endCursor,
    startCursor,
  } = data;

  const renderInviteButton = (
    variant: "default" | "outline" = "outline",
    className = "h-8",
    showIcon = true,
  ) => {
    const handleClick = () => {
      if (me?.isSuperCounselor && hasSeatsAvailable) {
        navigate(getRoute("sendInvite"));
      }
    };

    const buttonText =
      variant === "default" ? t("invited.emptyButton") : t("inviteTeammate");
    const ariaLabel =
      variant === "default"
        ? `${t("invited.emptyButton")} - ${t("invited.empty")}`
        : undefined;

    const button = (
      <Button
        className={className}
        type="button"
        variant={variant}
        onClick={handleClick}
        aria-label={ariaLabel}
      >
        {showIcon && <PlusCircle className="h-4 w-4" aria-label={buttonText} />}
        {buttonText}
      </Button>
    );

    if (me?.isSuperCounselor && !hasSeatsAvailable) {
      return <SeatAlert showAlert={true}>{button}</SeatAlert>;
    }

    return (
      <PermissionAlert showAlert={!me?.isSuperCounselor}>
        {button}
      </PermissionAlert>
    );
  };

  return (
    <InviteTableContext.Provider value={{ refetch }}>
      <DataTable
        data={invites}
        columns={columns}
        isLoading={loading || orgLoading}
        pagination={
          <>
            <DataTable.PaginationLabel
              pageCount={getPageCount(totalCount)}
              pageIndex={pageIndex}
            />
            <DataTable.PaginationControls
              onNextPage={
                hasNextPage &&
                (() => {
                  setBefore(null);
                  setAfter(endCursor || null);
                  setPageIndex(pageIndex + 1);
                })
              }
              onPreviousPage={
                hasPreviousPage &&
                (() => {
                  setAfter(null);
                  setBefore(startCursor || null);
                  setPageIndex(pageIndex - 1);
                })
              }
            />
          </>
        }
        tableHeader={<DataTable.THeader />}
        tableBody={<DataTable.TBody />}
        emptyState={
          <EmptyState
            copy={t("invited.empty")}
            button={renderInviteButton(
              "default",
              "mt-8 h-10 bg-green-700 hover:bg-green-800",
              false,
            )}
            icon={
              <UserSearch
                className="size-9 text-primary-light"
                aria-label={t("inviteTeammate")}
              />
            }
          />
        }
        topHeader={
          <>
            <DataTable.Title
              title={t("title")}
              description={t("description", {
                seats: org?.seatsAvailable ?? 0,
              })}
            />
            <div className="flex items-center gap-2">
              <DataTable.Filter
                customFilters={[
                  {
                    id: "role",
                    label: t("role"),
                    values: Object.values(DashInviteRoleEnum).map((type) => ({
                      value: type,
                      renderComponent: ({ value }) =>
                        i18n.t(ROLE_MAP[value as DashInviteRoleEnum]),
                    })),
                    avatarFilterLabel: false,
                  },
                ]}
                onFilterChange={handleFilterChange}
                displayValue={filterRoles
                  .map((value) => i18n.t(ROLE_MAP[value as DashInviteRoleEnum]))
                  .join(", ")}
              />
              {renderInviteButton()}
            </div>
          </>
        }
      />
    </InviteTableContext.Provider>
  );
};

function getPageCount(totalCount: number) {
  return Math.ceil(totalCount / DEFAULT_ROWS);
}
